import React, { useEffect, useMemo, useState } from "react";
import { Check, Circle, ChevronRight, Loader2, Link2, Copy, RotateCcw, RefreshCw, Receipt, Mail, Download, ChevronDown, ChevronUp, CheckCircle2, XCircle, Wrench, Send, Unlink, Repeat, FileText, Upload, ClipboardCheck, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SalesReadonlyWorkflow from "@/components/SalesReadonlyWorkflow";
import EmailApprovalDialog from "@/components/EmailApprovalDialog";
import StageDocuments from "@/components/StageDocuments";
import { useStageSentFiles } from "@/components/tracker/useStageSentFiles";
import { OrderConfirmationSend } from "@/components/OrderConfirmationSend";
import { openSentFile } from "@/components/SentDocumentsPanel";
import { invoicedPercent, nextFreeKind, sentToCustomer, invoicePdfUrl } from "@/components/tracker/invoiceRules";
import { DeleteInvoiceButton, FullyBilledNote } from "@/components/tracker/DeleteInvoiceButton";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

// Iter89k (2026-02-24) — Inline-Ausklapp-Panel für AZ2 / AZ3 / AZ2_combined.
// Editierbarer Prozentsatz + Empfänger-E-Mail, analog zum AZ1-Panel. Wird
// in ProjectStatusTracker auf drei Stufen (down_payment_received /
// installation_start / commissioning) eingehängt.
const InvoicePanel2Inline = ({
  signedOffer, project, kind, percent, setPercent,
  email, setEmail, onSubmit, onCreateOnly, onCancel, sending,
}) => {
  const totalGross = Number(signedOffer?.total_gross || 0);
  const usePercent = kind === "AZ2_combined" ? 50 : (Number(percent) || 0);
  const invoiceAmount = totalGross * (usePercent / 100);
  const kindLabel = kind === "AZ2_combined" ? "Kombiniert 50 % (30 % + 20 %)"
    : kind === "AZ2" ? "2. Anzahlungsrechnung (AZ2)"
    : kind === "AZ3" ? "3. Anzahlungsrechnung (AZ3)"
    : "Anzahlungsrechnung";
  return (
    <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-2.5" data-testid={`invoice-panel2-${kind}`}>
      <div className="flex items-center gap-2 text-[12px] font-semibold text-zinc-100">
        <Receipt size={13} className="text-[#00E575]" /> {kindLabel}
      </div>
      <div className="text-[11.5px] text-zinc-400">
        Auftragssumme (brutto): <b className="text-zinc-100">{totalGross.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</b>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-zinc-400">Prozentsatz</span>
          <input
            type="number"
            min={1}
            max={100}
            step={1}
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            disabled={kind === "AZ2_combined"}
            data-testid={`invoice-panel2-percent-${kind}`}
            className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none disabled:opacity-60 disabled:cursor-not-allowed"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-zinc-400">Rechnungsbetrag (brutto)</span>
          <div className="h-10 px-3 rounded-lg bg-[#18181B] border border-zinc-800 text-[13px] text-zinc-100 inline-flex items-center font-semibold">
            {invoiceAmount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
        </label>
      </div>
      {kind === "AZ2_combined" && (
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Kombiniert: 30 % zahlbar mit Montagebeginn ({(totalGross*0.3).toLocaleString("de-DE",{minimumFractionDigits:2})} €), 20 % zahlbar mit Inbetriebnahme ({(totalGross*0.2).toLocaleString("de-DE",{minimumFractionDigits:2})} €). Prozentsatz fest bei 50 %.
        </div>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-[12px] text-zinc-400">Empfänger-E-Mail (optional – sonst Kunden-Mail)</span>
        <input
          type="email"
          placeholder={project?.customer_email || "kunde@example.de"}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={`invoice-panel2-email-${kind}`}
          className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none"
        />
      </label>
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={onSubmit}
          disabled={sending}
          data-testid={`invoice-panel2-send-${kind}`}
          className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[140px]"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Freigeben &amp; senden</>}
        </button>
        <button
          onClick={onCreateOnly}
          disabled={sending}
          data-testid={`invoice-panel2-create-only-${kind}`}
          className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[12.5px] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Download size={13} /> Nur erstellen
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          data-testid={`invoice-panel2-cancel-${kind}`}
          className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export const ProjectStatusTracker = ({ project, customer, offers = [], onUpdate }) => {
  const { user } = useAuth();
  const isManager = ["admin", "sales_manager", "sales_director", "super_admin"].includes(user?.role) || !!user?.is_super_admin;
  const canUnassignInstaller = user?.role === "admin" || user?.role === "sales_director";
  const [stages, setStages] = useState([]);
  const [advancing, setAdvancing] = useState(null);
  const [note, setNote] = useState("");
  const [noteFor, setNoteFor] = useState(null);
  const [invoicePanel, setInvoicePanel] = useState(false);
  const [invoicePercent, setInvoicePercent] = useState(50);
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceListOpen, setInvoiceListOpen] = useState(true);
  // Iter89k (2026-02-24) — AZ2/AZ3-Panel: editierbarer Prozentsatz, analog
  // zum AZ1-Panel. Kind kann "AZ2", "AZ3" oder "AZ2_combined" sein.
  const [invoicePanel2Open, setInvoicePanel2Open] = useState(false);
  const [invoicePanel2Kind, setInvoicePanel2Kind] = useState("AZ2");
  const [invoicePanel2Percent, setInvoicePanel2Percent] = useState(30);
  const [invoicePanel2Email, setInvoicePanel2Email] = useState("");
  const [installers, setInstallers] = useState([]);
  const [handoverPanel, setHandoverPanel] = useState(false);
  const [handoverInstallerId, setHandoverInstallerId] = useState("");
  const [handoverEmail, setHandoverEmail] = useState("");
  const [handoverSending, setHandoverSending] = useState(false);
  const [paidUpdating, setPaidUpdating] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  // Iter88zp (2026-02-19) — Rechnungs-PDF im Storage mit AKTUELLEM
  // Template neu rendern. Nummer/Datum bleiben unverändert. Wird nach
  // Layout-Änderungen (z. B. 2-Seiten → 1-Seite) einmalig geklickt.
  const regeneratePdf = async (inv) => {
    if (!signedOffer || !inv?.id || !isManager) return;
    const keeps = sentToCustomer(inv, customer?.email);
    if (!window.confirm(`Rechnungs-PDF ${inv.invoice_number} mit aktuellem Layout neu rendern? Die Rechnungsnummer bleibt gleich. ${keeps ? "Das Rechnungsdatum bleibt (bereits an den Kunden versendet)." : "Das Rechnungsdatum wird auf heute gesetzt."}`)) return;
    setRegenerating(inv.id);
    try {
      const r = await api.post(`/offers/${signedOffer.id}/invoices/${inv.id}/regenerate-pdf`);
      toast.success(`Rechnung ${r.data?.invoice_number} neu gerendert${r.data?.date_kept ? " – Datum unverändert" : " – Datum heute"}`);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerierung fehlgeschlagen");
    } finally {
      setRegenerating(null);
    }
  };
  const [bzaSending, setBzaSending] = useState(false);
  const [bzaUploading, setBzaUploading] = useState(false);
  // Iter89i (2026-02-21) — Sende-Status für „Auftragsbestätigung an Kunden".
  // User-Bugreport: „Gar keine Reaktion" beim Klick. Ursache: `window.confirm`
  // wurde offenbar von Safari/Popup-Blockern unterdrückt → keine Rückmeldung.
  // Fix: eigener Loading-State + sofortiger Toast, ohne blockierendes confirm.
  const [customerBzaSending, setCustomerBzaSending] = useState(false);
  const [customerBzaConfirmOpen, setCustomerBzaConfirmOpen] = useState(false);
  const [handoverBackOfficeOpen, setHandoverBackOfficeOpen] = useState(false);
  const [handoverBackOfficeNote, setHandoverBackOfficeNote] = useState("");
  const [handoverBackOfficeSending, setHandoverBackOfficeSending] = useState(false);

  // Pick the signed offer (most recent)
  const signedOffer = useMemo(
    () => (offers || []).filter((o) => o.status === "signed").sort((a, b) => (b.signed_at || "").localeCompare(a.signed_at || ""))[0] || null,
    [offers],
  );
  const existingInvoices = useMemo(() => signedOffer?.invoices || [], [signedOffer]);
  const remainingPct = Math.max(0, 100 - invoicedPercent(existingInvoices));
  const nextKind = nextFreeKind(existingInvoices);
  const canDeleteInvoices = ["admin", "sales_director", "super_admin"].includes(user?.role) || !!user?.is_super_admin;
  const firstPaidInvoice = useMemo(
    () => existingInvoices.find((i) => i.paid_at) || null,
    [existingInvoices],
  );
  const existingHandovers = project?.installer_handovers || [];

  // Iter89d (2026-02-20) — E-Mail-Send-Logs des Projekts. Damit der
  // Tracker für die Stufe „Auftragsbestätigung versendet" den echten
  // Sende-Status zeigen kann (versendet / nicht versendet / Fehler). Ohne
  // diese Info bleibt der User im Ungewissen — genau die Situation, die
  // der Kunde bei Projekt Pizzimento gemeldet hat (Iter89d Bug-Report).
  const [emailLogs, setEmailLogs] = useState([]);
  useEffect(() => {
    if (!project?.id) return;
    api.get(`/projects/${project.id}/email-logs`)
      .then((r) => setEmailLogs(Array.isArray(r.data?.logs) ? r.data.logs : []))
      .catch(() => setEmailLogs([]));
  }, [project?.id, offers?.length]);

  // Letzten Auftragsbestätigungs-Send finden (beide Kinds akzeptieren:
  // `order_confirmation_pdf` = manueller Re-Send-Flow via post_signature,
  // `order_confirmation` = automatischer Send beim Signieren via offers.py).
  const lastOrderConfirmationLog = useMemo(() => {
    return emailLogs.find(
      (l) => l.kind === "order_confirmation_pdf" || l.kind === "order_confirmation"
    ) || null;
  }, [emailLogs]);

  // Versendete Dokumente je Stufe (Auftragsbestätigung, BzA-Pakete, Rechnungen).
  const sentFilesByStage = useStageSentFiles(project?.id, existingInvoices, emailLogs);

  useEffect(() => {
    api.get("/tracker/stages").then((r) => setStages(r.data.stages || [])).catch(() => {});
    if (isManager) {
      api.get("/installers").then((r) => {
        const list = r.data || [];
        setInstallers(list);
        const def = list.find((i) => i.is_default) || list[0];
        if (def) setHandoverInstallerId(def.id);
      }).catch(() => {});
    }
  }, [isManager]);

  const tracker = project?.tracker || { current_stage: "lead", stages: [{ key: "lead", completed: true }] };
  const completedKeys = new Set((tracker.stages || []).filter((s) => s.completed).map((s) => s.key));
  const completedAt = Object.fromEntries((tracker.stages || []).map((s) => [s.key, { at: s.completed_at, note: s.note }]));
  const currentIdx = stages.findIndex((s) => s.key === tracker.current_stage);

  const advance = async (stageKey, withNote = null) => {
    setAdvancing(stageKey);
    try {
      await api.post(`/projects/${project.id}/tracker/advance`, { stage: stageKey, note: withNote });
      toast.success("Status aktualisiert");
      setNote("");
      setNoteFor(null);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally {
      setAdvancing(null);
    }
  };

  const revert = async (stageKey) => {
    if (!window.confirm(`Status wirklich zurücksetzen auf '${stages.find((s) => s.key === stageKey)?.label}'?`)) return;
    setAdvancing(stageKey);
    try {
      await api.post(`/projects/${project.id}/tracker/revert`, { stage: stageKey });
      toast.success("Status zurückgesetzt");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    } finally {
      setAdvancing(null);
    }
  };

  const sendDownPaymentInvoice = async ({ sendEmail, kind, percent, cc, recipientOverride }) => {
    if (!signedOffer) return;
    if (!isManager) return; // only managers may invoice
    setInvoiceSending(true);
    try {
      const usePct = Math.max(1, Math.min(100, Number(percent ?? invoicePercent) || 50));
      const body = { percent: usePct, send_email: !!sendEmail };
      if (kind) body.kind = kind;
      const effectiveRecipient =
        (recipientOverride && recipientOverride.trim()) ||
        (invoiceEmail && invoiceEmail.trim()) || "";
      if (sendEmail && effectiveRecipient) body.recipient_email = effectiveRecipient;
      if (sendEmail && Array.isArray(cc) && cc.length) body.cc = cc;
      const r = await api.post(`/offers/${signedOffer.id}/down-payment-invoice`, body);
      const inv = r.data?.invoice;
      if (sendEmail) {
        if (inv?.email_error) {
          toast.warning(`Rechnung ${inv.invoice_number} erstellt, Versand fehlgeschlagen: ${inv.email_error}`);
        } else if (inv?.recipient_email) {
          // Iter88zs — voll-transparente Erfolgsmeldung: Empfänger + ALLE CCs.
          const cc_line = (Array.isArray(cc) && cc.length) ? ` · CC: ${cc.join(", ")}` : "";
          toast.success(
            `✓ Rechnung ${inv.invoice_number} an ${inv.recipient_email}${cc_line} versendet`,
            { duration: 8000 },
          );
        } else {
          toast.warning(`Rechnung ${inv?.invoice_number} erstellt – kein E-Mail-Empfänger`);
        }
      } else {
        toast.success(`Rechnung ${inv?.invoice_number} erstellt`);
      }
      setInvoicePanel(false);
      setInvoicePanel2Open(false);
      setInvoiceEmail("");
      setInvoicePanel2Email("");
      setApprovalOpen(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rechnung konnte nicht erstellt werden");
    } finally {
      setInvoiceSending(false);
    }
  };

  // Iter88zl (2026-02-19) — WICHTIG: „Erneut senden" nutzt einen eigenen
  // Endpoint, der die bestehende Rechnung (inkl. Nummer + PDF) wieder
  // an den Kunden schickt, OHNE eine neue Rechnungsnummer/PDF/DB-Zeile
  // zu erzeugen. Der Create-Endpoint würde sonst eine Duplikat-Rechnung
  // AZ(n+1) anlegen (Bug aus test_reports/iteration_91.json).
  const resendInvoiceEmail = async ({ invoiceId, cc, recipientOverride }) => {
    if (!signedOffer || !invoiceId || !isManager) return;
    setInvoiceSending(true);
    try {
      const recipient = (recipientOverride && recipientOverride.trim()) || "";
      if (!recipient) {
        toast.error("Empfänger-E-Mail fehlt");
        return;
      }
      const body = { recipient_email: recipient };
      if (Array.isArray(cc) && cc.length) body.cc = cc;
      const r = await api.post(`/offers/${signedOffer.id}/invoices/${invoiceId}/resend-email`, body);
      if (r.data?.email_error) {
        toast.warning(`Erneuter Versand ${r.data?.invoice_number} fehlgeschlagen: ${r.data.email_error}`);
      } else {
        // Iter88zs — voll-transparente Erfolgsmeldung: Empfänger + ALLE CCs.
        const cc_line = (Array.isArray(cc) && cc.length) ? ` · CC: ${cc.join(", ")}` : "";
        toast.success(
          `✓ Rechnung ${r.data?.invoice_number} an ${r.data?.recipient_email}${cc_line} versendet`,
          { duration: 8000 },
        );
      }
      setApprovalOpen(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erneuter Versand fehlgeschlagen");
    } finally {
      setInvoiceSending(false);
    }
  };

  // Iter88zi (2026-02-19) — Approval-Dialog vor dem tatsächlichen Send.
  // Der Rep sieht Empfänger + CC-Vorschläge + Anhang-Preview und muss
  // explizit „Jetzt senden" klicken. Kein stiller Auto-Versand mehr.
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalCtx, setApprovalCtx] = useState(null);
  // Iter88zk (2026-02-19) — Reopen-Modal (Undo für erledigte Stufen).
  const [reopenStage, setReopenStage] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenSaving, setReopenSaving] = useState(false);
  const doReopen = async () => {
    if (!reopenStage) return;
    setReopenSaving(true);
    try {
      await api.post(`/projects/${project.id}/stages/${reopenStage.key}/reopen`, { reason: reopenReason || null });
      toast.success(`Stufe „${reopenStage.label}" wieder geöffnet`);
      setReopenStage(null); setReopenReason("");
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wieder-öffnen fehlgeschlagen");
    } finally { setReopenSaving(false); }
  };
  const openApproval = ({ kind, percent, resendOf, prefilledRecipient, invoiceId }) => {
    if (!signedOffer) return;
    // Iter88zk-fix (2026-02-19) — Bei „Erneut senden" nutzen wir die
    // zuletzt verwendete Adresse aus dem Rechnungs-Datensatz. Der State
    // `invoiceEmail` ist dann leer, weil das Ausklapp-Panel nicht offen
    // ist. Fallback-Reihenfolge:
    //   1. explizit übergebene prefilledRecipient (bei Resend)
    //   2. aktuell im Panel getippte E-Mail
    //   3. Kundenprofil-E-Mail (aus customer prop oder project.customer_email)
    const recipient =
      (prefilledRecipient && prefilledRecipient.trim()) ||
      (invoiceEmail && invoiceEmail.trim()) ||
      customerEmail(project) || "";
    if (!recipient) {
      toast.error("Bitte zuerst eine Empfänger-E-Mail eintragen");
      return;
    }
    const usePct = Math.max(1, Math.min(100, Number(percent ?? invoicePercent) || 50));
    const kindLabel = kind === "AZ2_combined" ? "2. Abschlagsrechnung (50 %)"
      : kind === "AZ2" ? "2. Anzahlungsrechnung"
      : kind === "AZ3" ? "3. Anzahlungsrechnung"
      : "1. Anzahlungsrechnung";
    // Iter88zl (2026-02-19) — Anrede aus dem tatsächlich geladenen
    // Kunden-Datensatz (customer prop) statt aus project?.customer,
    // welches vom Backend nicht mitgeliefert wird. Verhindert die
    // "Sehr geehrte Familie ," Anzeige aus test_reports/iteration_91.json.
    const custLast = (customer?.last_name || "").trim();
    const custFirst = (customer?.first_name || "").trim();
    const sal = (customer?.salutation || "").toString().toLowerCase();
    let anrede;
    if (sal === "male" && custLast) anrede = `Sehr geehrter Herr ${custLast}`;
    else if (sal === "female" && custLast) anrede = `Sehr geehrte Frau ${custLast}`;
    else if (custLast) anrede = `Sehr geehrte Familie ${custLast}`;
    else if (custFirst) anrede = `Sehr geehrte/r ${custFirst}`;
    else anrede = "Sehr geehrte/r Kunde/Kundin";
    // Iter88zm (2026-02-19) — User-Wunsch: der Resend zeigt KEINEN
    // „⚠ WIEDERHOLTER VERSAND"-Hinweis mehr. Falls der Erst-Versand
    // z. B. an eine Test-Adresse ging, hat der Kunde noch nie eine
    // Mail bekommen — der zweite Versand ist für ihn die erste echte.
    const bodyPreview = (
      `${anrede},\n\n` +
      `vielen Dank für Ihr Vertrauen in die NOVATUS Energie GmbH und Ihren Auftrag, ` +
      `der mit der Zusage der KfW rechtswirksam geworden ist.\n\n` +
      `Anbei erhalten Sie die vereinbarte ${kindLabel} zu Ihrem Wärmepumpenprojekt.\n\n` +
      `Nach Zahlungseingang beginnen wir umgehend mit der weiteren Projektbearbeitung ` +
      `(raumweise Heizlastberechnung, Materialdisposition, Installation).\n\n` +
      `Mit freundlichen Grüßen\nChristof Schirm\nGeschäftsführer NOVATUS Energie GmbH`
    );
    setApprovalCtx({
      kind: kind || "AZ1",
      percent: usePct,
      recipient,
      isResend: !!resendOf,
      invoiceId: invoiceId || null,
      subject: `${kindLabel} · ${signedOffer.offer_number}${kind ? `-${kind}` : "-AZ1"} · NOVATUS Energie`,
      attachments: [{ name: `Rechnung_${signedOffer.offer_number}-${kind || "AZ1"}.pdf` }],
      bodyPreview,
    });
    setApprovalOpen(true);
  };

  // Iter89k (2026-02-24) — Öffnet das inline-Ausklapp-Panel für AZ2 / AZ3 /
  // AZ2_combined mit editierbarem Prozentsatz + Empfänger-E-Mail. Nach dem
  // Klick auf „Freigeben & senden" wird das globale Approval-Modal geöffnet.
  const openInvoicePanel2 = (kind, defaultPct) => {
    setInvoicePanel2Kind(kind);
    setInvoicePanel2Percent(kind === "AZ2_combined" ? defaultPct : Math.min(defaultPct, remainingPct));
    setInvoicePanel2Email("");
    setInvoicePanel2Open(true);
  };
  const submitInvoicePanel2 = () => {
    const pct = Math.max(1, Math.min(100, Number(invoicePanel2Percent) || 0));
    if (!pct) { toast.error("Bitte gültigen Prozentsatz eingeben"); return; }
    // AZ2_combined ist fix 50 % (30 + 20). Prozente werden ignoriert.
    const finalPct = invoicePanel2Kind === "AZ2_combined" ? 50 : pct;
    openApproval({
      kind: invoicePanel2Kind,
      percent: finalPct,
      prefilledRecipient: invoicePanel2Email || undefined,
    });
  };
  const createInvoicePanel2Only = async () => {
    const pct = Math.max(1, Math.min(100, Number(invoicePanel2Percent) || 0));
    if (!pct) { toast.error("Bitte gültigen Prozentsatz eingeben"); return; }
    const finalPct = invoicePanel2Kind === "AZ2_combined" ? 50 : pct;
    await sendDownPaymentInvoice({
      sendEmail: false,
      kind: invoicePanel2Kind,
      percent: finalPct,
      recipientOverride: invoicePanel2Email,
    });
    setInvoicePanel2Open(false);
  };
  const customerEmail = (p) => customer?.email || p?.customer_email || p?.customer?.email || "";

  const publicUrl = project?.public_tracking_token
    ? `${window.location.origin}/track/${project.public_tracking_token}`
    : null;

  const toggleInvoicePaid = async (inv) => {
    if (!signedOffer || !isManager) return;
    const willMarkPaid = !inv.paid_at;
    if (willMarkPaid && !window.confirm(`Anzahlungsrechnung ${inv.invoice_number} als BEZAHLT markieren?`)) return;
    if (!willMarkPaid && !window.confirm(`Bezahlt-Markierung für ${inv.invoice_number} wirklich entfernen?`)) return;
    setPaidUpdating(inv.id);
    try {
      await api.post(`/offers/${signedOffer.id}/invoices/${inv.id}/mark-paid`, { paid: willMarkPaid });
      toast.success(willMarkPaid ? "Als bezahlt markiert" : "Bezahlt-Markierung entfernt");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setPaidUpdating(null);
    }
  };

  // Iter88zz9c — Zahlungseingangs-Bestätigung an Kunden mit Fortschritts-Link.
  const [confirmingPayment, setConfirmingPayment] = useState(null);

  // Iter88zz9h — KfW-Bescheid vom Kunden erfassen (Datum + optional PDF).
  const [kfwPanel, setKfwPanel] = useState(false);
  const [kfwDate, setKfwDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [kfwNote, setKfwNote] = useState("");
  const [kfwFile, setKfwFile] = useState(null);
  const [kfwSaving, setKfwSaving] = useState(false);
  const saveKfwConfirmation = async () => {
    if (!isManager) return;
    if (!kfwDate) return toast.error("Bitte Datum angeben");
    setKfwSaving(true);
    try {
      const form = new FormData();
      form.append("received_at", kfwDate);
      if (kfwNote.trim()) form.append("note", kfwNote.trim());
      if (kfwFile) form.append("file", kfwFile);
      await api.post(`/projects/${project.id}/kfw-confirmation`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("KfW-Bescheid gespeichert");
      setKfwPanel(false); setKfwFile(null); setKfwNote("");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setKfwSaving(false);
    }
  };

  // Iter88zz9h — Heizlast-Termin fixieren (Datum + Uhrzeit + Berater).
  const [hlPanel, setHlPanel] = useState(false);
  const [hlDateTime, setHlDateTime] = useState("");
  const [hlConsultant, setHlConsultant] = useState("");
  const [hlEmail, setHlEmail] = useState("");
  const [hlPhone, setHlPhone] = useState("");
  const [hlNote, setHlNote] = useState("");
  const [hlSaving, setHlSaving] = useState(false);
  const saveHeatLoadAppointment = async () => {
    if (!isManager) return;
    if (!hlDateTime) return toast.error("Bitte Datum + Uhrzeit angeben");
    setHlSaving(true);
    try {
      await api.post(`/projects/${project.id}/heat-load-appointment`, {
        scheduled_at: new Date(hlDateTime).toISOString(),
        consultant_name: hlConsultant.trim() || null,
        consultant_email: hlEmail.trim() || null,
        consultant_phone: hlPhone.trim() || null,
        note: hlNote.trim() || null,
      });
      toast.success("Termin gespeichert");
      setHlPanel(false); setHlDateTime(""); setHlConsultant(""); setHlEmail(""); setHlPhone(""); setHlNote("");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setHlSaving(false);
    }
  };

  const sendPaymentConfirmation = async (inv) => {
    if (!signedOffer || !isManager) return;
    const to = customer?.email || "";
    if (!to) return toast.error("Kein E-Mail-Kontakt am Kunden hinterlegt");
    if (!window.confirm(
      `Zahlungseingangs-Bestätigung für ${inv.invoice_number} an ${to} senden?\n\n` +
      `Der Kunde erhält eine E-Mail mit Bestätigung + Link auf den Projekt-Fortschritt.`
    )) return;
    setConfirmingPayment(inv.id);
    try {
      const r = await api.post(`/offers/${signedOffer.id}/invoices/${inv.id}/send-payment-confirmation`);
      toast.success(`Bestätigung an ${r.data?.sent_to || to} versendet`);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setConfirmingPayment(null);
    }
  };

  const sendHandover = async (cc) => {
    if (!isManager) return;
    if (!handoverInstallerId) return toast.error("Bitte Installation wählen");
    setHandoverSending(true);
    try {
      const body = {
        installer_id: handoverInstallerId,
        send_email: true,
      };
      if (handoverEmail.trim()) body.recipient_email = handoverEmail.trim();
      if (Array.isArray(cc) && cc.length) body.cc = cc;
      const r = await api.post(`/projects/${project.id}/handover-installer`, body);
      const h = r.data?.handover || {};
      if (h.email_error) {
        toast.warning(`Übergabe erstellt, Versand fehlgeschlagen: ${h.email_error}`);
      } else if (h.recipient_email) {
        const cc_hint = (Array.isArray(cc) && cc.length) ? ` (CC: ${cc.length})` : "";
        toast.success(`Übergabe an ${h.installer_name} (${h.recipient_email})${cc_hint} versendet`);
      } else {
        toast.success(`Übergabe-PDF erstellt – kein Mail-Empfänger hinterlegt`);
      }
      setHandoverPanel(false);
      setHandoverEmail("");
      setHandoverApprovalOpen(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übergabe fehlgeschlagen");
    } finally {
      setHandoverSending(false);
    }
  };

  // Iter88zl (2026-02-19) — Approval-Dialog auch vor dem Installateur-
  // Übergabe-Versand. Ermöglicht CC (Buchhaltung/Innendienst) und
  // manuelle Freigabe analog zum AZ-Rechnungsflow.
  const [handoverApprovalOpen, setHandoverApprovalOpen] = useState(false);
  const [handoverApprovalCtx, setHandoverApprovalCtx] = useState(null);
  const openHandoverApproval = () => {
    if (!handoverInstallerId) return toast.error("Bitte Installation wählen");
    const installer = installers.find((i) => i.id === handoverInstallerId);
    const recipient = (handoverEmail.trim() || installer?.email || "").trim();
    if (!recipient) {
      toast.error("Bitte eine Empfänger-E-Mail für die Übergabe eintragen");
      return;
    }
    const projectNo = project?.project_number || "—";
    const custLast = (customer?.last_name || "").trim();
    const custFirst = (customer?.first_name || "").trim();
    const custName = [custFirst, custLast].filter(Boolean).join(" ") || "Kunde";
    setHandoverApprovalCtx({
      recipient,
      subject: `Installateur-Übergabe · Projekt ${projectNo} · NOVATUS Energie`,
      attachments: [{ name: `Installateur_Uebergabe_${projectNo.replace(/[\s/]/g, "_")}.pdf` }],
      bodyPreview:
        `Sehr geehrte Damen und Herren,\n\n` +
        `die NOVATUS Energie GmbH übergibt Ihnen hiermit das Projekt ${projectNo} ` +
        `(Kunde: ${custName}) zur Umsetzung. Anbei finden Sie das vollständige ` +
        `Übergabe-Dokument als PDF mit allen relevanten Kunden-, Gebäude- und ` +
        `Technik-Informationen sowie der eingebetteten Foto-Dokumentation.\n\n` +
        `Bitte setzen Sie sich für die Terminabstimmung direkt mit dem zuständigen ` +
        `NOVATUS-Vertriebsmitarbeiter in Verbindung. Kontaktdaten und alle weiteren ` +
        `Informationen sind im angehängten PDF aufgeführt.\n\n` +
        `Mit freundlichen Grüßen\nNOVATUS Energie GmbH`,
    });
    setHandoverApprovalOpen(true);
  };

  const unassignInstaller = async () => {
    if (!canUnassignInstaller) return;
    if (!project?.installer_id) return;
    const reason = window.prompt(
      "Installation-Zuordnung aufheben?\n\n" +
      "Der bisherige Upload-Link wird ungültig, ein neuer Token wird generiert.\n" +
      "Bitte kurz einen Grund eintragen (optional).",
      "",
    );
    if (reason === null) return; // cancelled
    try {
      await api.post(`/projects/${project.id}/unassign-installer`, { reason });
      toast.success("Installation-Zuordnung aufgehoben");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aufhebung fehlgeschlagen");
    }
  };

  const [changingInstaller, setChangingInstaller] = useState(false);
  const [changePickerOpen, setChangePickerOpen] = useState(false);

  const changeInstaller = async (newInstallerId, newName) => {
    if (!canUnassignInstaller || !newInstallerId) return;
    if (!window.confirm(
      `Installation auf "${newName}" wechseln?\n\n` +
      "Der alte Upload-Link wird ungültig. Ein neuer Token für den neuen Installation wird generiert.\n\n" +
      "Hinweis: Es wird KEIN Übergabe-PDF automatisch versendet. Falls gewünscht, anschließend separat \"An Installation übergeben\" auslösen."
    )) return;
    setChangingInstaller(true);
    try {
      const r = await api.post(`/projects/${project.id}/change-installer`, { installer_id: newInstallerId });
      const d = r.data || {};
      toast.success(
        `Installation gewechselt → ${d.new_installer_name}`,
        { description: d.previous_installer_name ? `Vorher: ${d.previous_installer_name}` : "Neue Zuordnung aktiv.", duration: 5000 },
      );
      setChangePickerOpen(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setChangingInstaller(false);
    }
  };

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link in Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen – bitte manuell markieren");
    }
  };

  const sendBzaPackage = async () => {
    if (!isManager || !signedOffer) return;
    if (!window.confirm("BzA-Datenpaket an den Energieberater senden?\n\nEnthält: BzA-Excel + Liefer-/Leistungsvertrag + unterschriebenes Angebot.")) return;
    setBzaSending(true);
    try {
      const r = await api.post(`/projects/${project.id}/bza-package/send`);
      if (r.data?.ok) {
        toast.success("BzA-Paket an Energieberater versendet");
      } else {
        toast.warning(`Paket gespeichert, Versand fehlgeschlagen: ${r.data?.error || "unbekannt"}`);
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "BzA-Versand fehlgeschlagen");
    } finally {
      setBzaSending(false);
    }
  };

  // Iter89i (2026-02-21) — „Auftragsbestätigung an Kunden versenden".
  // Ersetzt das alte inline-onClick mit window.confirm (das in Safari
  // stumm geblockt wurde). Zeigt beim Start sofort einen Toast, damit der
  // Benutzer weiß, dass der Klick ankam.
  const sendCustomerBzaPackage = async () => {
    if (customerBzaSending) return;
    if (!signedOffer?.id) {
      toast.error("Kein signiertes Angebot gefunden — bitte Seite neu laden.");
      return;
    }
    if (!customer?.email) {
      toast.error("Kunden-E-Mail fehlt — bitte im Kundenprofil pflegen.");
      return;
    }
    setCustomerBzaConfirmOpen(false);
    setCustomerBzaSending(true);
    const _pending = toast.loading(`Sende Auftragsbestätigung an ${customer.email} …`);
    try {
      const r = await api.post(`/offers/${signedOffer.id}/customer-bza-package/send`, {});
      toast.dismiss(_pending);
      if (r.data?.ok) {
        toast.success(`Auftragsbestätigung an ${customer.email} versendet.`);
        onUpdate?.();
      } else {
        toast.error(r.data?.error || "Versand fehlgeschlagen");
      }
    } catch (e) {
      toast.dismiss(_pending);
      const msg = e?.response?.data?.detail || e?.message || "Versand fehlgeschlagen";
      toast.error(String(msg));
      // eslint-disable-next-line no-console
      console.error("[customer-bza-package] send failed", e);
    } finally {
      setCustomerBzaSending(false);
    }
  };

  const uploadSignedBza = async (file) => {
    if (!file) return;
    setBzaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${project.id}/bza/upload-signed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.ok) {
        toast.success("Signiertes BzA-PDF hochgeladen");
        onUpdate?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBzaUploading(false);
    }
  };

  const setBzaStatus = async (status, note = "") => {
    try {
      await api.patch(`/projects/${project.id}/bza-status`, { status, note });
      toast.success("BzA-Status aktualisiert");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status konnte nicht gesetzt werden");
    }
  };

  const submitBackOfficeHandover = async () => {
    if (!signedOffer) return;
    setHandoverBackOfficeSending(true);
    try {
      const r = await api.post(`/projects/${project.id}/sales-handover`, { note: handoverBackOfficeNote || "" });
      if (r.data?.ok) {
        if (r.data.already_done) {
          toast.info("Wurde bereits übergeben");
        } else {
          toast.success("Projekt an Back-Office übergeben — viel Erfolg beim nächsten Auftrag! 🎉");
        }
      }
      setHandoverBackOfficeOpen(false);
      setHandoverBackOfficeNote("");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übergabe fehlgeschlagen");
    } finally {
      setHandoverBackOfficeSending(false);
    }
  };

  if (!stages.length) return null;

  const canRevert = user?.role === "admin" || user?.role === "sales_manager" || user?.role === "sales_director";

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="project-status-tracker">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575] mb-0.5">Auftragsstatus</div>
          <h2 className="font-display text-lg font-semibold">Wo steht das Projekt?</h2>
        </div>
        {publicUrl && (
          <button
            onClick={copyPublicUrl}
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] font-medium inline-flex items-center gap-1.5 flex-shrink-0"
            data-testid="copy-public-link"
            title="Tracking-Link für den Kunden kopieren"
          >
            <Link2 size={14} /> Kunden-Link <Copy size={12} />
          </button>
        )}
      </div>

      {/* Vertical timeline */}
      <ol className="relative space-y-3" data-testid="stage-list">
        {stages.map((stage, idx) => {
          const done = completedKeys.has(stage.key);
          const isCurrent = stage.key === tracker.current_stage;
          const isNext = !done && idx === currentIdx + 1;
          const meta = completedAt[stage.key];
          // Iter88zz9e — visuelle Trennung in drei Sektionen anhand order_index:
          //   Vertriebsphase (<= 40)  ·  Backoffice (50..100)  ·  Ausführung (>= 108)
          const oi = stage.order_index ?? 0;
          const prevOi = idx > 0 ? (stages[idx - 1].order_index ?? 0) : -1;
          const sectionFor = (o) => (o <= 42 ? "vertrieb" : o <= 100 ? "backoffice" : "ausfuehrung");
          const section = sectionFor(oi);
          const prevSection = idx === 0 ? null : sectionFor(prevOi);
          const showSectionHeader = idx === 0 || section !== prevSection;
          const sectionTitle = {
            vertrieb:    { title: "Vertriebsphase", color: "text-[#00E575]", accent: "border-[#00E575]/30", icon: "▸" },
            backoffice:  { title: "Backoffice",     color: "text-sky-300",   accent: "border-sky-500/30",   icon: "▸" },
            ausfuehrung: { title: "Ausführung",     color: "text-amber-300", accent: "border-amber-500/30", icon: "▸" },
          }[section];
          return (
            <React.Fragment key={stage.key}>
              {showSectionHeader && (
                <li className={`flex items-center gap-2 pt-1 pb-1 border-t ${sectionTitle.accent} ${idx === 0 ? "border-t-0" : "mt-2"}`} aria-hidden="true">
                  <span className={`text-[10px] font-mono uppercase tracking-[0.18em] font-bold ${sectionTitle.color}`}>
                    {sectionTitle.icon} {sectionTitle.title}
                  </span>
                  <span className="text-[10px] text-zinc-600 font-mono">
                    {stages.filter((s) => sectionFor(s.order_index ?? 0) === section).length} Stufen
                  </span>
                </li>
              )}
            <li
              data-testid={`stage-${stage.key}`}
              className={`flex gap-3 p-3 rounded-xl border transition ${
                isCurrent
                  ? "bg-[#00E575]/8 border-[#00E575]/40"
                  : done
                  ? "bg-[#09090B]/50 border-zinc-800"
                  : "bg-transparent border-zinc-800/60"
              }`}
            >
              <div
                className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  done
                    ? "bg-[#00E575] text-[#09090B]"
                    : isNext
                    ? "bg-zinc-800 text-[#00E575] border-2 border-[#00E575]/60"
                    : "bg-zinc-900 text-zinc-600"
                }`}
              >
                {done ? <Check size={18} /> : <Circle size={16} className={isNext ? "" : "opacity-50"} />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <div className={`font-display font-bold text-[15px] min-w-0 ${done ? "text-white" : isNext ? "text-zinc-100" : "text-zinc-500"}`}>
                    {stage.label}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {done && meta?.at && (
                      <div className="text-[12px] text-zinc-300 font-mono">{fmtDate(meta.at)}</div>
                    )}
                    {done && isManager && (
                      <button
                        onClick={() => setReopenStage({ key: stage.key, label: stage.label })}
                        title="Diese Stufe wieder öffnen"
                        data-testid={`stage-${stage.key}-reopen`}
                        className="h-6 px-1.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[11px] font-bold inline-flex items-center gap-1"
                      >
                        <RotateCcw size={11} /> Undo
                      </button>
                    )}
                  </div>
                </div>
                <div className={`text-[12px] mt-0.5 ${done ? "text-zinc-400" : isNext ? "text-zinc-400" : "text-zinc-400"}`}>
                  {stage.description}
                </div>
                {meta?.note && (
                  <div className="text-[12px] text-zinc-300 mt-1.5 bg-[#09090B]/60 border border-zinc-800 rounded-lg px-2.5 py-1.5 font-medium">
                    {meta.note}
                  </div>
                )}

                {/* Iter88zk (2026-02-19) — Dokumenten-Upload/Liste pro Stufe.
                    Storage-Endpoint aus iter88zj, kein 16-MB-Limit mehr. */}
                {(done || isNext || sentFilesByStage[stage.key]?.length > 0) && (
                  <StageDocuments
                    projectId={project.id}
                    stageKey={stage.key}
                    canUpload={isManager || isNext}
                    sentFiles={sentFilesByStage[stage.key]}
                  />
                )}

                {/* Action: advance to this stage */}
                {!done && isNext && (stage.key !== "order_confirmation_sent" || isManager) && (
                  <div className="mt-2.5 space-y-2">
                    {noteFor === stage.key ? (
                      <>
                        <input
                          autoFocus
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Optional: kurze Notiz (z. B. Liefertermin)"
                          className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                          data-testid={`stage-note-input-${stage.key}`}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => advance(stage.key, note.trim() || null)}
                            disabled={advancing === stage.key}
                            data-testid={`stage-confirm-${stage.key}`}
                            className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                          >
                            {advancing === stage.key ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Bestätigen</>}
                          </button>
                          <button
                            onClick={() => { setNoteFor(null); setNote(""); }}
                            className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[13px]"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => setNoteFor(stage.key)}
                        data-testid={`stage-advance-${stage.key}`}
                        className="h-10 px-3.5 rounded-lg bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 font-semibold text-[13px] hover:bg-[#00E575]/25 active:scale-95 transition inline-flex items-center gap-1.5"
                      >
                        <ChevronRight size={15} /> Auf '{stage.label}' setzen
                      </button>
                    )}
                  </div>
                )}
                {/* Revert option (managers only) for the current stage */}
                {done && isCurrent && idx > 0 && canRevert && (
                  <button
                    onClick={() => revert(stages[idx - 1].key)}
                    data-testid={`stage-revert-${stage.key}`}
                    className="mt-2 text-[12px] text-zinc-300 hover:text-amber-400 inline-flex items-center gap-1"
                    title="Auf vorherige Stufe zurücksetzen"
                  >
                    <RotateCcw size={11} /> Auf vorherige Stufe zurück
                  </button>
                )}

                {/* Sales-Handover an Back-Office (Phase 2): offer_signed stage, sales-rep + manager */}
                {stage.key === "offer_signed" && done && signedOffer && user?.role !== "installer" && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="sales-handover-block">
                    {project?.sales_handover?.done_at ? (
                      <div className="rounded-xl bg-[#00E575]/10 border border-[#00E575]/40 p-3" data-testid="sales-handover-done-pill">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-[#00E575] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-bold text-[#00E575]">An Back-Office übergeben</div>
                            <div className="text-[12px] text-zinc-400 mt-0.5">
                              {new Date(project.sales_handover.done_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              {project.sales_handover.done_by_name && <> · {project.sales_handover.done_by_name}</>}
                            </div>
                            {project.sales_handover.note && (
                              <div className="text-[12px] text-zinc-300 mt-1 italic">&bdquo;{project.sales_handover.note}&ldquo;</div>
                            )}
                            <div className="text-[10.5px] text-zinc-500 mt-1.5">
                              Das Back-Office übernimmt jetzt Rechnungsstellung, BzA, Inbetriebnahme & BnD. Du behältst Lese-Zugriff.
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-[#0F2010] border border-[#00E575]/30 p-3" data-testid="sales-handover-cta">
                        <div className="flex items-start gap-2 mb-2">
                          <ClipboardCheck size={16} className="text-[#00E575] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-bold text-zinc-100">Sales abgeschlossen?</div>
                            <div className="text-[12px] text-zinc-400 mt-0.5 leading-snug">
                              Wenn alle Unterlagen vom Kunden vorliegen, gib das Projekt jetzt frei für Back-Office. Du bekommst dann den nächsten Lead — und Buchhaltung/Berater übernimmt Rechnungen, BzA & BnD.
                            </div>
                          </div>
                        </div>
                        {handoverBackOfficeOpen ? (
                          <div className="space-y-2 mt-2">
                            <label className="flex flex-col gap-1">
                              <span className="text-[12px] text-zinc-400">Optionale Notiz fürs Back-Office</span>
                              <textarea
                                value={handoverBackOfficeNote}
                                onChange={(e) => setHandoverBackOfficeNote(e.target.value)}
                                rows={2}
                                placeholder="z. B. Sondertermin mit Energieberater bitte vermeiden"
                                data-testid="sales-handover-note"
                                className="bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-[12.5px] text-zinc-100 focus:border-[#00E575] outline-none resize-none"
                              />
                            </label>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={submitBackOfficeHandover}
                                disabled={handoverBackOfficeSending}
                                data-testid="sales-handover-confirm-btn"
                                className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[170px]"
                              >
                                {handoverBackOfficeSending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> Jetzt übergeben</>}
                              </button>
                              <button
                                onClick={() => { setHandoverBackOfficeOpen(false); setHandoverBackOfficeNote(""); }}
                                disabled={handoverBackOfficeSending}
                                className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]"
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setHandoverBackOfficeOpen(true)}
                            data-testid="sales-handover-open-btn"
                            className="w-full h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition inline-flex items-center justify-center gap-1.5"
                          >
                            <Send size={13} /> An Back-Office übergeben
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Read-only Status für Sales-Rep (für Kundenanfragen) */}
                {stage.key === "offer_signed" && done && signedOffer && !isManager && user?.role !== "installer" && (
                  <SalesReadonlyWorkflow projectId={project.id} />
                )}

                {/* Iter89d (2026-02-20) — Auftragsbestätigung-Sende-Status.
                    Bug: User hat auf Prod gesendet, ohne zu wissen ob die
                    Mail rausging (Kunde ohne E-Mail). Der Tracker zeigt
                    jetzt direkt unter der „Auftragsbestätigung versendet"-
                    Stufe den echten Sende-Status (grün/amber/rot). */}
                {stage.key === "order_confirmation_sent" && (done || isCurrent || isNext) && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="order-confirmation-send-status">
                    <div className="text-[11.5px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5 inline-flex items-center gap-1.5">
                      <Mail size={12} /> Auftragsbestätigung — Versandstatus
                    </div>
                    {lastOrderConfirmationLog ? (
                      lastOrderConfirmationLog.ok ? (
                        <div className="rounded-lg border border-[#00E575]/30 bg-[#00E575]/8 px-3 py-2 text-[12.5px] text-[#00E575]" data-testid="order-conf-status-ok">
                          <div className="font-bold inline-flex items-center gap-1.5"><CheckCircle2 size={13}/> Versendet</div>
                          <div className="text-[11.5px] text-[#00E575]/85 mt-0.5 font-mono">
                            an <b>{lastOrderConfirmationLog.recipient_email || "—"}</b>
                            {lastOrderConfirmationLog.sent_at && (
                              <> · {new Date(lastOrderConfirmationLog.sent_at).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}</>
                            )}
                            {lastOrderConfirmationLog.provider && <> · via {lastOrderConfirmationLog.provider}</>}
                          </div>
                          {lastOrderConfirmationLog.note && (
                            <div className="text-[11.5px] text-zinc-300 mt-1 whitespace-pre-line">Notiz: {lastOrderConfirmationLog.note}</div>
                          )}
                          {(lastOrderConfirmationLog.storage_path || lastOrderConfirmationLog.attachments?.length) && (
                            <button type="button" onClick={() => openSentFile(project.id, lastOrderConfirmationLog.id, 0)}
                              data-testid="order-conf-open-pdf"
                              className="mt-2 h-8 px-3 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold inline-flex items-center gap-1.5">
                              <Eye size={12} /> Versendete Auftragsbestätigung ansehen
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200" data-testid="order-conf-status-failed">
                          <div className="font-bold inline-flex items-center gap-1.5"><XCircle size={13}/> NICHT versendet</div>
                          <div className="text-[11.5px] text-red-200/85 mt-0.5">
                            Grund: <b>{lastOrderConfirmationLog.error || "Unbekannter Fehler"}</b>
                          </div>
                          {(!customer?.email) && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] text-red-100/80">Kein Kunden-E-Mail hinterlegt.</span>
                              <button
                                type="button"
                                onClick={() => {
                                  // Iter89e — Direkt zum Kunden-Tab im Projekt springen.
                                  // Der Tab-Query-Param wird von ProjectDetail beim
                                  // Mount ausgelesen und aktiviert den Customer-Tab.
                                  window.location.assign(`/projects/${project.id}?tab=customer`);
                                }}
                                data-testid="order-conf-fix-customer-email"
                                className="h-8 px-3 rounded-lg bg-red-500 hover:bg-red-400 text-white text-[12px] font-bold inline-flex items-center gap-1.5"
                              >
                                <Pencil size={12}/> Kunden-E-Mail pflegen
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    ) : (
                      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12.5px] text-amber-200" data-testid="order-conf-status-none">
                        <div className="font-bold inline-flex items-center gap-1.5"><Circle size={12}/> Bisher nicht versendet</div>
                        <div className="text-[11.5px] text-amber-100/75 mt-0.5">
                          Sobald die Auftragsbestätigung an den Kunden verschickt wurde, erscheint hier der Zeitstempel.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Iter89d/89e (2026-02-20) — AZ1-Shortcut auf order_confirmed.
                    Der volle Rechnungs-Panel selbst lebt weiterhin unter der
                    Stufe `first_az_sent`; dieser Shortcut öffnet ihn und
                    scrollt hin, damit der Manager NICHT auf die KfW-Bestätigung
                    warten muss. */}
                {stage.key === "order_confirmed" && signedOffer && isManager && remainingPct > 0 && (
                  <div className="mt-2 rounded-lg border border-[#00E575]/25 bg-[#00E575]/6 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="az1-shortcut">
                    <Receipt size={13} className="text-[#00E575] flex-shrink-0"/>
                    <div className="text-[12px] text-zinc-200 flex-1 min-w-0">
                      <b>Nächster Schritt:</b> {nextKind === "AZ1" ? "1. Anzahlungsrechnung" : `Anzahlungsrechnung ${nextKind}`} erstellen und an Kunden senden — auch schon vor dem KfW-Bescheid möglich.
                    </div>
                    <button
                      onClick={() => {
                        setInvoicePanel(true);
                        setInvoicePercent(Math.min(50, remainingPct));
                        setTimeout(() => {
                          document.querySelector('[data-testid="invoice-panel"], [data-testid="open-down-payment-invoice"]')
                            ?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 60);
                      }}
                      data-testid="az1-shortcut-btn"
                      className="h-8 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] hover:bg-[#00C964] active:scale-95 inline-flex items-center gap-1"
                    >
                      <Mail size={12}/> Zur Anzahlungsrechnung
                    </button>
                  </div>
                )}

                {/* Down-payment invoice action (AZ1): managers only.
                    Iter89e — Panel jetzt auf jeder Stufe ab `order_confirmed`
                    sichtbar (order_confirmed / kfw_confirmation_received /
                    first_az_sent), damit die Rechnung nicht auf die KfW-
                    Bestätigung warten muss. Damit der Panel nicht dreifach
                    gerendert wird, hängt er an der ERSTEN sinnvollen Stufe:
                    - `first_az_sent`, sobald dort schon oder gerade aktiv,
                    - sonst `order_confirmed` (weil das die erste Stufe nach
                      der Signatur ist, an der AZ1 möglich wird). */}
                {(() => {
                  const azStages = ["order_confirmed", "kfw_confirmation_received", "first_az_sent"];
                  if (!azStages.includes(stage.key)) return false;
                  if (!signedOffer || !isManager) return false;
                  // Wählen: die Stufe, die den Panel rendert. Wenn die
                  // aktuelle Pipeline-Stage bereits >= first_az_sent, hängen
                  // wir es dort auf. Sonst am order_confirmed.
                  const _cur = stages.findIndex((s) => s.key === tracker.current_stage);
                  const _faz = stages.findIndex((s) => s.key === "first_az_sent");
                  const wantsFirstAz = _cur >= _faz && _faz >= 0;
                  const targetKey = wantsFirstAz ? "first_az_sent" : "order_confirmed";
                  return stage.key === targetKey;
                })() && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                        <Receipt size={13} className="text-[#00E575]" /> Anzahlungsrechnungen
                      </div>
                      {!invoicePanel && remainingPct > 0 && (
                        <button
                          onClick={() => {
                            setInvoicePanel(true);
                            setInvoicePercent(Math.min(50, remainingPct));
                          }}
                          data-testid="open-down-payment-invoice"
                          className="w-full sm:w-auto min-h-9 py-1.5 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-semibold text-[12.5px] leading-tight hover:bg-[#00C964] active:scale-95 transition inline-flex items-center justify-center gap-1.5"
                        >
                          <Mail size={13} /> {nextKind === "AZ1" ? "Erste Anzahlungsrechnung versenden" : `Anzahlungsrechnung ${nextKind} erstellen`}
                        </button>
                      )}
                    </div>
                    {remainingPct <= 0 && <div className="mb-2"><FullyBilledNote testid="invoices-fully-billed" /></div>}

                    {invoicePanel && (
                      <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-2.5 mb-2" data-testid="invoice-panel">
                        <div className="text-[12px] text-zinc-300 font-medium">
                          Auftragssumme (brutto): <b className="text-white">{Number(signedOffer.total_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</b>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex flex-col gap-1">
                            <span className="text-[12px] text-zinc-400">Prozentsatz</span>
                            <input
                              type="number"
                              min={1}
                              max={remainingPct || 100}
                              step={1}
                              value={invoicePercent}
                              onChange={(e) => setInvoicePercent(e.target.value)}
                              data-testid="invoice-percent-input"
                              className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none"
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-[12px] text-zinc-400">Rechnungsbetrag (brutto)</span>
                            <div className="h-10 px-3 rounded-lg bg-[#18181B] border border-zinc-800 text-[13px] text-zinc-100 inline-flex items-center font-semibold">
                              {(Number(signedOffer.total_gross || 0) * (Number(invoicePercent) || 0) / 100)
                                .toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                          </label>
                        </div>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Empfänger-E-Mail (optional – sonst Kunden-Mail)</span>
                          <input
                            type="email"
                            placeholder={(project?.customer_email) || "kunde@example.de"}
                            value={invoiceEmail}
                            onChange={(e) => setInvoiceEmail(e.target.value)}
                            data-testid="invoice-recipient-input"
                            className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none"
                          />
                        </label>

                        {/* Confirmation strip + preview – matches the Offer-Send modal pattern */}
                        <div className="rounded-lg bg-[#18181B] border border-zinc-800 px-3 py-2 flex items-center gap-2 text-[12px] font-medium" data-testid="invoice-send-to-strip">
                          <Mail size={14} className="text-[#00E575] shrink-0" />
                          <span className="text-zinc-400">Wird gesendet an:</span>
                          <span className="text-zinc-100 font-semibold truncate">
                            {(invoiceEmail.trim() || project?.customer_email || "—")}
                          </span>
                        </div>
                        <div className="rounded-lg bg-[#09090B] border border-zinc-800 p-2.5 text-[11.5px] text-zinc-400 leading-relaxed">
                          <div className="font-semibold text-zinc-200 mb-1 text-[12px]">Was bekommt der Kunde?</div>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Rechnungs-PDF im Anhang (mit Bankverbindung &amp; QR-Code)</li>
                            <li>Begleittext mit Erläuterung &amp; nächsten Schritten</li>
                            <li>Link zum Projekt-Status-Tracker</li>
                          </ul>
                        </div>

                        <div className="flex flex-wrap gap-2 pt-1">
                          <button
                            onClick={() => openApproval({})}
                            disabled={invoiceSending}
                            data-testid="invoice-send-btn"
                            className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[140px]"
                          >
                            {invoiceSending ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Freigeben &amp; senden</>}
                          </button>
                          <button
                            onClick={() => sendDownPaymentInvoice({ sendEmail: false })}
                            disabled={invoiceSending}
                            data-testid="invoice-create-only-btn"
                            className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[12.5px] disabled:opacity-50 inline-flex items-center gap-1.5"
                          >
                            <Download size={13} /> Nur erstellen
                          </button>
                          <button
                            onClick={() => { setInvoicePanel(false); setInvoiceEmail(""); }}
                            disabled={invoiceSending}
                            className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}

                    {existingInvoices.length > 0 && (
                      <div className="bg-[#09090B]/60 border border-zinc-800 rounded-xl">
                        <button
                          onClick={() => setInvoiceListOpen((v) => !v)}
                          className="w-full flex items-center justify-between px-3 py-2 text-[11.5px] text-zinc-400 hover:text-zinc-200"
                          data-testid="invoice-list-toggle"
                        >
                          <span>Bisherige Rechnungen ({existingInvoices.length})</span>
                          {invoiceListOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {existingInvoices.length > 0 && invoiceListOpen && (
                          <ul className="px-3 pb-2 space-y-2">
                            {existingInvoices.map((inv) => {
                              const isCombined = inv.kind === "AZ2_combined" || (inv.tranches && inv.tranches.length > 0);
                              return (
                              <li key={inv.id} data-testid={`invoice-row-${inv.id}`} className="bg-zinc-900/40 rounded-lg p-2.5">
                                <div className="flex items-center justify-between gap-2 text-[12px] text-zinc-200 font-medium">
                                  <div className="min-w-0">
                                    <div className="font-mono truncate flex items-center gap-2">
                                      {inv.invoice_number}
                                      {isCombined && (
                                        <span className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400 border border-zinc-700 rounded-md px-1 py-0.5">kombiniert</span>
                                      )}
                                      {isManager && inv.paid_at && (
                                        <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 rounded-md px-1.5 py-0.5">
                                          <CheckCircle2 size={10} /> komplett bezahlt
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[10.5px] text-zinc-500">
                                      {isManager ? <>{inv.percent}% · {Number(inv.total_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</> : <>{inv.percent}%</>}
                                      {inv.sent_at && inv.recipient_email ? ` · gesendet an ${inv.recipient_email}` : ""}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {isManager && !isCombined && (
                                      <button
                                        onClick={() => toggleInvoicePaid(inv)}
                                        disabled={paidUpdating === inv.id}
                                        data-testid={`invoice-mark-paid-${inv.id}`}
                                        className={`h-8 px-2 rounded-lg inline-flex items-center gap-1 text-[12px] font-semibold transition disabled:opacity-50 ${
                                          inv.paid_at ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700" : "bg-[#00E575]/20 text-[#00E575] hover:bg-[#00E575]/30"
                                        }`}
                                      >
                                        {paidUpdating === inv.id ? <Loader2 size={11} className="animate-spin" /> : inv.paid_at ? <><XCircle size={11} /> Bezahlt rückgängig</> : <><CheckCircle2 size={11} /> Als bezahlt</>}
                                      </button>
                                    )}
                                    {/* Iter88zz9c — Zahlungseingangs-Bestätigung an Kunden mit Projekt-Fortschritts-Link. */}
                                    {isManager && inv.paid_at && customer?.email && (
                                      <button
                                        onClick={() => sendPaymentConfirmation(inv)}
                                        disabled={confirmingPayment === inv.id}
                                        title={`Zahlungsbestätigung + Fortschritts-Link an ${customer.email} senden`}
                                        data-testid={`invoice-send-payment-confirmation-${inv.id}`}
                                        className="h-8 px-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1 text-[11.5px] font-semibold disabled:opacity-50"
                                      >
                                        {confirmingPayment === inv.id ? <Loader2 size={11} className="animate-spin" /> : <><Send size={12} /> Zahlung bestätigen</>}
                                      </button>
                                    )}
                                    {inv.pdf_storage_path && (
                                      <a href={invoicePdfUrl(fileUrl, inv)} target="_blank" rel="noopener noreferrer" className="h-8 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1 text-[11.5px]" data-testid={`invoice-download-${inv.id}`}>
                                        <Download size={12} /> PDF
                                      </a>
                                    )}
                                    {/* Iter88zp — PDF mit aktuellem Layout neu rendern (Manager-only). */}
                                    {isManager && inv.pdf_storage_path && (
                                      <button
                                        onClick={() => regeneratePdf(inv)}
                                        disabled={regenerating === inv.id}
                                        title="PDF mit aktuellem Layout neu rendern (Nummer bleibt, Datum nur bei an den Kunden versendeten Rechnungen)"
                                        data-testid={`invoice-regenerate-${inv.id}`}
                                        className="h-8 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-zinc-100 inline-flex items-center gap-1 text-[11.5px] disabled:opacity-50"
                                      >
                                        {regenerating === inv.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                                      </button>
                                    )}
                                    {/* Iter88zk — Erneut versenden mit Approval-Modal (CC etc.).
                                        Iter88zq (2026-02-19) — Empfänger-Default = KUNDEN-E-Mail.
                                        Frühere Test-Versände (last_resent_recipient_email) werden
                                        NICHT mehr als Default vorgeschlagen, damit der Kunden-Versand
                                        nicht versehentlich an eine Test-Adresse geht. */}
                                    {isManager && (customer?.email || inv.recipient_email || inv.last_resent_recipient_email) && (
                                      <button
                                        onClick={() => openApproval({
                                          kind: inv.kind || "AZ1",
                                          percent: inv.percent || 50,
                                          resendOf: inv.invoice_number,
                                          prefilledRecipient: customer?.email || inv.recipient_email || inv.last_resent_recipient_email,
                                          invoiceId: inv.id,
                                        })}
                                        title={`Mail (erneut) senden an Kunden-Adresse${customer?.email ? ` (${customer.email})` : ""}`}
                                        data-testid={`invoice-resend-${inv.id}`}
                                        className="h-8 px-2 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-300 inline-flex items-center gap-1 text-[11.5px] font-semibold"
                                      >
                                        <Mail size={12} /> {inv.recipient_email || inv.last_resent_recipient_email ? "Erneut senden" : "Senden"}
                                      </button>
                                    )}
                                    {canDeleteInvoices && inv.kind !== "SR" && !inv.paid_at && !(inv.tranches || []).some((t) => t.paid_at) && !sentToCustomer(inv, customer?.email) && (
                                      <DeleteInvoiceButton offerId={signedOffer.id} inv={inv} onDeleted={onUpdate} />
                                    )}
                                  </div>
                                </div>
                                {/* Tranche-level paid markers */}
                                {isCombined && isManager && (
                                  <ul className="mt-2 space-y-1.5 border-t border-zinc-800 pt-2">
                                    {(inv.tranches || []).map((tr, ti) => {
                                      const trKey = `${inv.id}:${ti}`;
                                      return (
                                        <li key={ti} className="flex items-center justify-between gap-2 text-[11.5px] text-zinc-300">
                                          <div className="min-w-0">
                                            <div className="font-medium">
                                              {tr.percent}% · {Number(tr.amount_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                                              {tr.paid_at && <span className="ml-2 inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 rounded-md px-1.5 py-0.5"><CheckCircle2 size={10} /> bezahlt</span>}
                                            </div>
                                            <div className="text-[12px] text-zinc-300">{tr.due_label}{tr.paid_at ? ` · bezahlt am ${fmtDate(tr.paid_at)}` : ""}</div>
                                          </div>
                                          <button
                                            onClick={async () => {
                                              const willMark = !tr.paid_at;
                                              if (willMark && !window.confirm(`Tranche ${ti+1} (${tr.percent}%) als bezahlt markieren?`)) return;
                                              if (!willMark && !window.confirm(`Tranche ${ti+1} (${tr.percent}%) Bezahlt-Markierung entfernen?`)) return;
                                              setPaidUpdating(trKey);
                                              try {
                                                await api.post(`/offers/${signedOffer.id}/invoices/${inv.id}/mark-paid`, { paid: willMark, tranche_index: ti });
                                                toast.success(willMark ? "Tranche bezahlt" : "Markierung entfernt");
                                                onUpdate?.();
                                              } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
                                              finally { setPaidUpdating(null); }
                                            }}
                                            disabled={paidUpdating === trKey}
                                            data-testid={`tranche-mark-paid-${inv.id}-${ti}`}
                                            className={`h-7 px-2 rounded-md inline-flex items-center gap-1 text-[10.5px] font-semibold transition disabled:opacity-50 ${
                                              tr.paid_at ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700" : "bg-[#00E575]/20 text-[#00E575] hover:bg-[#00E575]/30"
                                            }`}
                                          >
                                            {paidUpdating === trKey ? <Loader2 size={10} className="animate-spin" /> : tr.paid_at ? <><XCircle size={10} /> Rückgängig</> : <><CheckCircle2 size={10} /> Als bezahlt</>}
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Auftragsbestätigung (PDF) direkt nach der Unterschrift versenden */}
                {stage.key === "order_confirmation_sent" && signedOffer && isManager && (
                  <OrderConfirmationSend
                    offerId={signedOffer.id}
                    projectId={project.id}
                    customerEmail={customer?.email || project?.customer_email || ""}
                    onSent={() => {
                      onUpdate?.();
                      api.get(`/projects/${project.id}/email-logs`)
                        .then((r) => setEmailLogs(Array.isArray(r.data?.logs) ? r.data.logs : []))
                        .catch(() => {});
                    }}
                  />
                )}

                {/* Iter88zz9f — BzA-Datenpaket beim Energieberater beantragen. */}
                {stage.key === "bza_requested" && signedOffer && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="bza-package-block">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                        <FileText size={13} className="text-[#00E575]" /> BzA-Datenpaket (Energieberater)
                      </div>
                      {(() => {
                        const bza = project?.bza || {};
                        const status = bza.status || "pending";
                        const statusMap = {
                          pending: { label: "Noch nicht versendet", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
                          sent_to_consultant: { label: "An Berater versendet", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
                          bza_received: { label: "BzA bei KfW eingegangen", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
                          bza_uploaded: { label: "BzA-PDF hochgeladen", cls: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" },
                        };
                        const cfg = statusMap[status] || statusMap.pending;
                        return (
                          <span data-testid="bza-status-badge" className={`text-[12px] font-bold uppercase tracking-wider rounded px-2 py-0.5 border ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="rounded-lg bg-[#09090B] border border-zinc-800 p-2.5 text-[11.5px] text-zinc-400 leading-relaxed mb-2">
                      Sendet 3 Dateien an den hinterlegten Energieberater:
                      <ul className="list-disc list-inside space-y-0.5 mt-1">
                        <li>BzA-Daten (Excel)</li>
                        <li>Liefer-/Leistungsvertrag (PDF)</li>
                        <li>Unterschriebenes Angebot (PDF)</li>
                      </ul>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={sendBzaPackage}
                        disabled={bzaSending}
                        data-testid="send-bza-package-btn"
                        className="flex-1 h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[180px]"
                      >
                        {bzaSending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> {project?.bza?.status === "sent_to_consultant" || project?.bza?.status === "bza_received" || project?.bza?.status === "bza_uploaded" ? "Erneut senden" : "BzA-Paket senden"}</>}
                      </button>
                      {project?.bza?.status === "sent_to_consultant" && (
                        <button
                          onClick={() => setBzaStatus("bza_received", "BzA von KfW bestätigt")}
                          data-testid="mark-bza-received-btn"
                          className="h-10 px-3 rounded-lg bg-sky-500/15 text-sky-300 border border-sky-500/30 hover:bg-sky-500/25 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                        >
                          <CheckCircle2 size={13} /> KfW bestätigt
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Iter88zz9f — Signierte BzA vom Energieberater hochladen (Stufe „BzA erhalten"). */}
                {stage.key === "order_confirmed" && signedOffer && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80 space-y-3" data-testid="auftragsbestaetigung-panel">
                    {/* Iter89h (2026-02-20) — Upload-Feld für die vom Energieberater
                        zurückgelieferte BzA. Positioniert direkt auf der Stufe
                        „Auftragsbestätigung" (User-Vorgabe Christof, 20.02.2026),
                        damit Upload + Versand-Button eine visuelle Einheit bilden. */}
                    <div className="rounded-lg bg-[#18181B] border border-zinc-800 p-2.5">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="text-[11.5px] text-zinc-300 font-semibold inline-flex items-center gap-1.5">
                          <Upload size={12} /> BzA vom Energieberater
                        </div>
                        {project?.bza?.signed_bza_storage_path && (
                          <a
                            href={fileUrl(project.bza.signed_bza_storage_path)}
                            target="_blank" rel="noopener noreferrer"
                            data-testid="signed-bza-download"
                            className="h-7 px-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1 text-[12px]"
                          >
                            <Download size={11} /> {project.bza.signed_bza_filename || "PDF"}
                          </a>
                        )}
                      </div>
                      <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold cursor-pointer">
                        {bzaUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        {project?.bza?.signed_bza_storage_path ? "Neue Version hochladen" : "BzA-PDF hochladen"}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          data-testid="bza-upload-input"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadSignedBza(f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>

                    {/* Iter89f/89h — Auftragsbestätigung an Kunden versenden.
                        3 Anhänge: BzA + Liefer-/Leistungsvertrag + Angebot
                        (Seiten 2–4). Button-Text laut User-Vorgabe:
                        „Auftragsbestätigung an Kunden versenden". */}
                    <div className={`rounded-lg border p-3 ${project?.bza?.signed_bza_storage_path ? "bg-[#00E575]/8 border-[#00E575]/40" : "bg-zinc-900/40 border-zinc-800"}`} data-testid="customer-bza-panel">
                      <div className="text-[12px] font-bold text-zinc-100 inline-flex items-center gap-1.5 mb-1">
                        <Send size={13} className="text-[#00E575]"/> BzA-Unterlagen an Kunden
                      </div>
                      <p className="text-[11.5px] text-zinc-400 leading-snug mb-2">
                        E-Mail an den Kunden mit den 3 Anlagen: <b>BzA</b>, <b>Liefer-/Leistungsvertrag</b> und <b>Angebot (Seite 2–4)</b> für die KfW-Einreichung.
                      </p>
                      {!project?.bza?.signed_bza_storage_path ? (
                        <div className="text-[11.5px] text-amber-300 font-medium">
                          Zuerst BzA-PDF oben hochladen — dann wird der Sende-Button aktiv.
                        </div>
                      ) : !customer?.email ? (
                        <div className="text-[11.5px] text-red-300 font-medium">
                          Kunden-E-Mail fehlt — bitte im Kundenprofil pflegen.
                        </div>
                      ) : !customerBzaConfirmOpen ? (
                        <button
                          onClick={() => setCustomerBzaConfirmOpen(true)}
                          disabled={customerBzaSending}
                          data-testid="send-customer-bza-package"
                          className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] active:scale-95 inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {customerBzaSending
                            ? <><Loader2 size={14} className="animate-spin"/> Wird versendet …</>
                            : <><Mail size={14}/> BzA-Unterlagen an Kunden versenden</>}
                        </button>
                      ) : (
                        <div className="rounded-lg bg-[#09090B] border border-[#00E575]/40 p-3 space-y-2.5" data-testid="customer-bza-confirm-panel">
                          <div className="text-[12px] text-zinc-200 leading-relaxed">
                            E-Mail inkl. <b>BzA</b>, <b>Liefer-/Leistungsvertrag</b> und <b>Angebot (Seite 2–4)</b> an <b>{customer.email}</b> senden?<br/>
                            Der Kunde erhält zusätzlich einen Button zum <b>Projekt-Status verfolgen</b>. Danach setzt die Pipeline automatisch auf „BzA an Kunden versendet".
                          </div>
                          <div className="flex flex-wrap gap-2 pt-0.5">
                            <button
                              onClick={sendCustomerBzaPackage}
                              disabled={customerBzaSending}
                              data-testid="send-customer-bza-package-confirm"
                              className="flex-1 h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[180px]"
                            >
                              {customerBzaSending
                                ? <><Loader2 size={14} className="animate-spin"/> Wird versendet …</>
                                : <><Send size={14}/> Jetzt senden</>}
                            </button>
                            <button
                              onClick={() => setCustomerBzaConfirmOpen(false)}
                              disabled={customerBzaSending}
                              className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px] disabled:opacity-50"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Iter88zz9h — KfW-Bescheid vom Kunden erfassen. */}
                {stage.key === "kfw_confirmation_received" && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="kfw-confirmation-block">
                    {project?.kfw_confirmation?.received_at ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#00E575]/8 border border-[#00E575]/30 p-2.5">
                        <div className="text-[12px] text-zinc-200">
                          <b>KfW-Bescheid erhalten:</b>{" "}
                          {new Date(project.kfw_confirmation.received_at).toLocaleDateString("de-DE")}
                          {project.kfw_confirmation.note ? <span className="text-zinc-400"> · {project.kfw_confirmation.note}</span> : null}
                        </div>
                        {project.kfw_confirmation.storage_path && (
                          <a href={fileUrl(project.kfw_confirmation.storage_path)} target="_blank" rel="noopener noreferrer"
                             data-testid="kfw-confirmation-download"
                             className="h-8 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1 text-[12px]">
                            <Download size={12} /> {project.kfw_confirmation.filename || "Bescheid"}
                          </a>
                        )}
                      </div>
                    ) : !kfwPanel ? (
                      <button
                        onClick={() => setKfwPanel(true)}
                        data-testid="open-kfw-confirmation"
                        className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] active:scale-95 transition inline-flex items-center gap-1.5"
                      >
                        <ClipboardCheck size={14} /> KfW-Bescheid vom Kunden erhalten – Datum eintragen
                      </button>
                    ) : (
                      <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-2.5" data-testid="kfw-confirmation-panel">
                        <div className="text-[11.5px] text-zinc-400">
                          Der Kunde hat den KfW-Förderbescheid erhalten und uns weitergeleitet. Erfasse hier das Datum und lade das Zusageschreiben hoch.
                        </div>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Datum des KfW-Bescheids</span>
                          <input type="date" value={kfwDate} onChange={(e) => setKfwDate(e.target.value)}
                                 data-testid="kfw-confirmation-date"
                                 className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Notiz (optional)</span>
                          <input type="text" value={kfwNote} onChange={(e) => setKfwNote(e.target.value)}
                                 data-testid="kfw-confirmation-note"
                                 placeholder="z.B. Zusage-Kennzeichen 458000000..."
                                 className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                        </label>
                        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-semibold cursor-pointer w-fit">
                          <Upload size={12} /> {kfwFile ? kfwFile.name : "Zusageschreiben (PDF) hochladen"}
                          <input type="file" accept="application/pdf,.pdf" className="hidden"
                                 data-testid="kfw-confirmation-file"
                                 onChange={(e) => setKfwFile(e.target.files?.[0] || null)} />
                        </label>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <button onClick={saveKfwConfirmation} disabled={kfwSaving}
                                  data-testid="kfw-confirmation-save"
                                  className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[160px]">
                            {kfwSaving ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} /> Bescheid speichern</>}
                          </button>
                          <button onClick={() => { setKfwPanel(false); setKfwFile(null); setKfwNote(""); }}
                                  disabled={kfwSaving}
                                  className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]">
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Iter88zz9h — Termin Heizlast + Verbaucheck. */}
                {stage.key === "heat_load_appointment" && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80" data-testid="heat-load-block">
                    {project?.heat_load_appointment?.scheduled_at ? (
                      <div className="rounded-lg bg-[#00E575]/8 border border-[#00E575]/30 p-2.5 text-[12px] text-zinc-200 space-y-0.5">
                        <div>
                          <b>Termin:</b>{" "}
                          {new Date(project.heat_load_appointment.scheduled_at).toLocaleString("de-DE", {
                            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
                          })} Uhr
                        </div>
                        {project.heat_load_appointment.consultant_name && (
                          <div className="text-zinc-400">
                            <b>Energieberater:</b> {project.heat_load_appointment.consultant_name}
                            {project.heat_load_appointment.consultant_email ? ` · ${project.heat_load_appointment.consultant_email}` : ""}
                            {project.heat_load_appointment.consultant_phone ? ` · ${project.heat_load_appointment.consultant_phone}` : ""}
                          </div>
                        )}
                        {project.heat_load_appointment.note && (
                          <div className="text-zinc-500 text-[11.5px]">{project.heat_load_appointment.note}</div>
                        )}
                        <button onClick={() => setHlPanel(true)}
                                data-testid="heat-load-edit"
                                className="mt-1 h-8 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1">
                          <Pencil size={11} /> Termin ändern
                        </button>
                      </div>
                    ) : !hlPanel ? (
                      <button
                        onClick={() => setHlPanel(true)}
                        data-testid="open-heat-load"
                        className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] active:scale-95 transition inline-flex items-center gap-1.5"
                      >
                        <Wrench size={14} /> Heizlast-Termin eintragen
                      </button>
                    ) : null}
                    {hlPanel && (
                      <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-2.5 mt-2" data-testid="heat-load-panel">
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Datum + Uhrzeit</span>
                          <input type="datetime-local" value={hlDateTime}
                                 onChange={(e) => setHlDateTime(e.target.value)}
                                 data-testid="heat-load-datetime"
                                 className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Energieberater (Name)</span>
                          <input type="text" value={hlConsultant}
                                 onChange={(e) => setHlConsultant(e.target.value)}
                                 data-testid="heat-load-consultant"
                                 placeholder="z.B. Herr Meyer"
                                 className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex flex-col gap-1">
                            <span className="text-[12px] text-zinc-400">E-Mail (optional)</span>
                            <input type="email" value={hlEmail}
                                   onChange={(e) => setHlEmail(e.target.value)}
                                   data-testid="heat-load-email"
                                   className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-[12px] text-zinc-400">Telefon (optional)</span>
                            <input type="tel" value={hlPhone}
                                   onChange={(e) => setHlPhone(e.target.value)}
                                   data-testid="heat-load-phone"
                                   className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                          </label>
                        </div>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Notiz (optional)</span>
                          <input type="text" value={hlNote}
                                 onChange={(e) => setHlNote(e.target.value)}
                                 data-testid="heat-load-note"
                                 placeholder="z.B. Kunde bringt Verbrauchsabrechnungen der letzten 3 Jahre mit"
                                 className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none" />
                        </label>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <button onClick={saveHeatLoadAppointment} disabled={hlSaving}
                                  data-testid="heat-load-save"
                                  className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[160px]">
                            {hlSaving ? <Loader2 size={14} className="animate-spin" /> : <><CheckCircle2 size={14} /> Termin speichern</>}
                          </button>
                          <button onClick={() => setHlPanel(false)} disabled={hlSaving}
                                  className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]">
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Installer handover action: shown on installer_handover stage once at least one invoice is paid */}
                {stage.key === "installer_handover" && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                        <Wrench size={13} className="text-[#00E575]" /> Übergabe an Installationsbetrieb
                      </div>
                      {isManager && firstPaidInvoice && !handoverPanel && (
                        <button
                          onClick={() => setHandoverPanel(true)}
                          data-testid="open-installer-handover"
                          className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-semibold text-[12.5px] hover:bg-[#00C964] active:scale-95 transition inline-flex items-center gap-1.5"
                        >
                          <Send size={13} /> An Installation übergeben
                        </button>
                      )}
                    </div>

                    {/* Gating-Hinweis */}
                    {isManager && !firstPaidInvoice && (
                      <div className="text-[11.5px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        Übergabe an den Installation ist erst möglich, sobald die erste Anzahlungsrechnung als bezahlt markiert wurde. Klicke dazu oben auf der Stufe „<b>1. Anzahlungsrechnung versendet</b>" → <b>Als bezahlt</b>.
                      </div>
                    )}
                    {!isManager && (
                      <div className="text-[11.5px] text-zinc-400 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2">
                        Diese Aktion wird vom Vertriebsleiter ausgelöst, sobald die Anzahlung des Kunden eingegangen ist.
                      </div>
                    )}

                    {/* Handover-Panel */}
                    {isManager && handoverPanel && (
                      <div className="bg-[#09090B] border border-[#00E575]/40 rounded-xl p-3 space-y-2.5 mb-2" data-testid="handover-panel">
                        <div className="text-[12px] text-zinc-300 font-medium">
                          Das Übergabe-PDF enthält alle Kunden-, Gebäude- und Technik-Daten inkl. Fotos – <b>ohne Preise</b>.
                        </div>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Installation</span>
                          <select
                            value={handoverInstallerId}
                            onChange={(e) => setHandoverInstallerId(e.target.value)}
                            data-testid="handover-installer-select"
                            className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none"
                          >
                            {installers.length === 0 && <option value="">— keine angelegt —</option>}
                            {installers.map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.name}{i.is_default ? "  ★" : ""}{i.email ? ` · ${i.email}` : ""}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[12px] text-zinc-400">Abweichende Empfänger-E-Mail (optional)</span>
                          <input
                            type="email"
                            placeholder={installers.find((i) => i.id === handoverInstallerId)?.email || "installateur@example.de"}
                            value={handoverEmail}
                            onChange={(e) => setHandoverEmail(e.target.value)}
                            data-testid="handover-email-input"
                            className="h-10 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-50 focus:border-[#00E575] outline-none"
                          />
                        </label>

                        {/* Confirmation strip + preview */}
                        <div className="rounded-lg bg-[#18181B] border border-zinc-800 px-3 py-2 flex items-center gap-2 text-[12px] font-medium" data-testid="handover-send-to-strip">
                          <Mail size={14} className="text-[#00E575] shrink-0" />
                          <span className="text-zinc-400">Wird gesendet an:</span>
                          <span className="text-zinc-100 font-semibold truncate">
                            {(handoverEmail.trim() || installers.find((i) => i.id === handoverInstallerId)?.email || "—")}
                          </span>
                        </div>
                        <div className="rounded-lg bg-[#09090B] border border-zinc-800 p-2.5 text-[11.5px] text-zinc-400 leading-relaxed">
                          <div className="font-semibold text-zinc-200 mb-1 text-[12px]">Was bekommt der Installation?</div>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Übergabe-PDF (Kunden-, Gebäude- &amp; Technik-Daten, <b>ohne Preise</b>)</li>
                            <li>Eingebettete Foto-Dokumentation aus dem Projekt</li>
                            <li>Sicherer Foto-/Video-Upload-Link für die Installation</li>
                          </ul>
                        </div>

                        <div className="flex flex-wrap gap-2 pt-1">
                          <button
                            onClick={openHandoverApproval}
                            disabled={handoverSending || !handoverInstallerId}
                            data-testid="handover-send-btn"
                            className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 min-w-[180px]"
                          >
                            {handoverSending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> PDF erstellen &amp; versenden</>}
                          </button>
                          <button
                            onClick={() => { setHandoverPanel(false); setHandoverEmail(""); }}
                            disabled={handoverSending}
                            className="h-10 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12.5px]"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    )}

                    {existingHandovers.length > 0 && (
                      <div className="bg-[#09090B]/60 border border-zinc-800 rounded-xl px-3 py-2 mt-2">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[11.5px] text-zinc-400">Bisherige Übergaben ({existingHandovers.length})</div>
                          {canUnassignInstaller && project?.installer_id && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setChangePickerOpen((v) => !v)}
                                data-testid="change-installer-btn"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-[12px] font-semibold"
                                title="Installation-Firma wechseln (Token wird rotiert)"
                              >
                                <Repeat size={11} /> Wechseln
                              </button>
                              <button
                                onClick={unassignInstaller}
                                data-testid="unassign-installer-btn"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-[12px] font-semibold"
                                title="Installation-Zuordnung aufheben (Token wird rotiert)"
                              >
                                <Unlink size={11} /> Aufheben
                              </button>
                            </div>
                          )}
                        </div>
                        {changePickerOpen && canUnassignInstaller && project?.installer_id && (
                          <div className="mb-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-1.5" data-testid="change-installer-picker">
                            <div className="text-[10.5px] font-semibold text-amber-200 uppercase tracking-wider mb-1">Auf welche Firma wechseln?</div>
                            {installers.filter((i) => i.id !== project.installer_id).length === 0 ? (
                              <div className="text-[12px] text-zinc-400">Keine weiteren Installations-Firmen vorhanden.</div>
                            ) : (
                              installers.filter((i) => i.id !== project.installer_id).map((i) => (
                                <button
                                  key={i.id}
                                  onClick={() => changeInstaller(i.id, i.name)}
                                  disabled={changingInstaller}
                                  data-testid={`change-installer-target-${i.id}`}
                                  className="w-full text-left px-2.5 py-1.5 rounded-md bg-zinc-900 hover:bg-zinc-800 text-[11.5px] text-zinc-200 inline-flex items-center justify-between gap-2 disabled:opacity-50"
                                >
                                  <span className="truncate">
                                    <span className="font-semibold">{i.name}</span>
                                    {i.city && <span className="text-zinc-500"> · {i.city}</span>}
                                  </span>
                                  {changingInstaller ? <Loader2 size={11} className="animate-spin flex-shrink-0" /> : <Repeat size={11} className="text-amber-400 flex-shrink-0" />}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                        <ul className="space-y-1.5">
                          {existingHandovers.map((h) => (
                            <li key={h.id} data-testid={`handover-row-${h.id}`} className="flex items-center justify-between gap-2 text-[12px] text-zinc-200 font-medium">
                              <div className="min-w-0">
                                <div className="truncate"><b>{h.installer_name}</b>{h.recipient_email ? ` · ${h.recipient_email}` : ""}</div>
                                <div className="text-[10.5px] text-zinc-500">
                                  {fmtDate(h.issued_at)}{h.email_error ? ` · Fehler: ${h.email_error}` : ""}
                                </div>
                              </div>
                              {h.pdf_storage_path && (
                                <a href={fileUrl(h.pdf_storage_path)} target="_blank" rel="noopener noreferrer" className="h-8 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1 text-[11.5px] flex-shrink-0">
                                  <Download size={12} /> PDF
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Iter89k (2026-02-24) — 2./3. Anzahlungsrechnung (Montagebeginn).
                    Öffnet ein editierbares Panel (kind + % + Empfänger) analog
                    zum AZ1-Panel. Prozentsätze sind per Default 30 % (AZ2) /
                    20 % (AZ3) / 50 % (kombiniert) und frei änderbar. */}
                {stage.key === "installation_start" && signedOffer && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80 space-y-2.5" data-testid="az2-invoice-block">
                    <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                      <Receipt size={13} className="text-[#00E575]" /> 2. + 3. Anzahlungsrechnung (Montagebeginn / Inbetriebnahme)
                    </div>
                    {existingInvoices.some((i) => i.kind === "AZ2_combined") ? (
                      <div className="text-[11.5px] text-zinc-300 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2">
                        Die kombinierte 2. Abschlagsrechnung (50 %) wurde bereits versendet. Tranchen bei Zahlungseingang oben unter „Auftrag unterschrieben" als bezahlt markieren.
                      </div>
                    ) : (
                      <>
                        <div className="text-[11.5px] text-zinc-400 leading-relaxed">
                          Wähle die passende Variante — jede Rechnung wird als PDF erstellt und
                          <b className="text-zinc-200"> automatisch per E-Mail an den Kunden versendet</b> (analog zur 1. Anzahlungsrechnung). Prozentsatz kann im Panel angepasst werden.
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <button
                            onClick={() => openInvoicePanel2("AZ2", 30)}
                            disabled={invoiceSending || remainingPct <= 0 || existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ2")}
                            data-testid="send-az2-single-btn"
                            className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                            title={existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ2") ? "AZ2 bereits erstellt" : "AZ2 einzeln erzeugen — % editierbar"}
                          >
                            <Mail size={13} /> AZ2 · 30 %
                          </button>
                          <button
                            onClick={() => openInvoicePanel2("AZ3", 20)}
                            disabled={invoiceSending || remainingPct <= 0 || existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ3")}
                            data-testid="send-az3-single-btn"
                            className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                            title={existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ3") ? "AZ3 bereits erstellt" : "AZ3 einzeln erzeugen — % editierbar"}
                          >
                            <Mail size={13} /> AZ3 · 20 %
                          </button>
                          <button
                            onClick={() => openInvoicePanel2("AZ2_combined", 50)}
                            disabled={invoiceSending || remainingPct < 50 || existingInvoices.some((i) => ["AZ2", "AZ3"].includes((i.kind || "").toUpperCase()))}
                            data-testid="send-az2-combined-btn"
                            className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold text-[12.5px] disabled:opacity-40 inline-flex items-center justify-center gap-1.5 border border-zinc-700"
                            title="Kombinierte 50 % (30 + 20) in EINER Rechnung mit zwei Tranchen"
                          >
                            <Mail size={13} /> Kombiniert 50 %
                          </button>
                        </div>
                        {invoicePanel2Open && ["AZ2","AZ3","AZ2_combined"].includes(invoicePanel2Kind) && (
                          <InvoicePanel2Inline
                            signedOffer={signedOffer}
                            project={project}
                            kind={invoicePanel2Kind}
                            percent={invoicePanel2Percent}
                            setPercent={setInvoicePanel2Percent}
                            email={invoicePanel2Email}
                            setEmail={setInvoicePanel2Email}
                            onSubmit={submitInvoicePanel2}
                            onCreateOnly={createInvoicePanel2Only}
                            onCancel={() => setInvoicePanel2Open(false)}
                            sending={invoiceSending}
                          />
                        )}
                        <div className="text-[11px] text-zinc-500 leading-relaxed">
                          <b className="text-zinc-400">Hinweis:</b> Bei „Kombiniert 50 %" wird EINE Rechnung mit zwei Tranchen erstellt (30 % zahlbar bei Montagebeginn, 20 % zahlbar bei Inbetriebnahme). Alternativ AZ2 und AZ3 einzeln versenden.
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Iter89k (2026-02-24) — 3. Anzahlungsrechnung (Inbetriebnahme). */}
                {stage.key === "commissioning" && signedOffer && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80 space-y-2" data-testid="az3-invoice-block">
                    <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                      <Receipt size={13} className="text-[#00E575]" /> 3. Anzahlungsrechnung (Inbetriebnahme)
                    </div>
                    {existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ3") ? (
                      <div className="text-[11.5px] text-zinc-300 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2">
                        Die 3. Anzahlungsrechnung (AZ3) wurde bereits versendet. Bezahlt-Markierung bitte oben unter „Auftrag unterschrieben" setzen, sobald die Zahlung eingegangen ist.
                      </div>
                    ) : existingInvoices.some((i) => i.kind === "AZ2_combined") ? (
                      <div className="text-[11.5px] text-zinc-300 bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2">
                        Die 20 %-Schlusszahlung ist bereits Teil der kombinierten 2. Abschlagsrechnung. Zweite Tranche der AZ2_combined bei Zahlungseingang als bezahlt markieren.
                      </div>
                    ) : (
                      <>
                        <div className="text-[11.5px] text-zinc-400 leading-relaxed">
                          Erstellt die AZ3 als PDF und versendet sie per E-Mail an den Kunden — Prozentsatz editierbar.
                        </div>
                        <button
                          onClick={() => openInvoicePanel2("AZ3", 20)}
                          disabled={invoiceSending || remainingPct <= 0}
                          data-testid="send-az3-btn"
                          className="h-10 w-full sm:w-auto px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                        >
                          <Mail size={13} /> AZ3 · 20 % freigeben &amp; senden
                        </button>
                        {invoicePanel2Open && invoicePanel2Kind === "AZ3" && (
                          <InvoicePanel2Inline
                            signedOffer={signedOffer}
                            project={project}
                            kind={invoicePanel2Kind}
                            percent={invoicePanel2Percent}
                            setPercent={setInvoicePanel2Percent}
                            email={invoicePanel2Email}
                            setEmail={setInvoicePanel2Email}
                            onSubmit={submitInvoicePanel2}
                            onCreateOnly={createInvoicePanel2Only}
                            onCancel={() => setInvoicePanel2Open(false)}
                            sending={invoiceSending}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Iter89k (2026-02-24) — Discovery-Shortcut auf „Anzahlung erhalten". */}
                {stage.key === "down_payment_received" && signedOffer && isManager && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/80 space-y-2" data-testid="az2-shortcut-block">
                    <div className="text-[12px] font-semibold text-zinc-200 inline-flex items-center gap-1.5">
                      <Receipt size={13} className="text-[#00E575]" /> Weitere Anzahlungsrechnungen (AZ2 / AZ3)
                    </div>
                    <div className="text-[11.5px] text-zinc-400 leading-relaxed">
                      Für die 2. Anzahlung (Montagebeginn) und 3. Anzahlung (Inbetriebnahme) — jeweils PDF + E-Mail an Kunde. Prozentsatz frei wählbar.
                    </div>
                    {remainingPct <= 0 && <FullyBilledNote testid="az2-shortcut-fully-billed" />}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <button
                        onClick={() => openInvoicePanel2("AZ2", 30)}
                        disabled={invoiceSending || remainingPct <= 0 || existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ2" || i.kind === "AZ2_combined")}
                        data-testid="send-az2-shortcut-btn"
                        className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                      >
                        <Mail size={13} /> AZ2 · 30 %
                      </button>
                      <button
                        onClick={() => openInvoicePanel2("AZ3", 20)}
                        disabled={invoiceSending || remainingPct <= 0 || existingInvoices.some((i) => (i.kind || "").toUpperCase() === "AZ3" || i.kind === "AZ2_combined")}
                        data-testid="send-az3-shortcut-btn"
                        className="h-10 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                      >
                        <Mail size={13} /> AZ3 · 20 %
                      </button>
                      <button
                        onClick={() => openInvoicePanel2("AZ2_combined", 50)}
                        disabled={invoiceSending || remainingPct < 50 || existingInvoices.some((i) => i.kind === "AZ2_combined" || (i.kind || "").toUpperCase() === "AZ2" || (i.kind || "").toUpperCase() === "AZ3")}
                        data-testid="send-az2-combined-shortcut-btn"
                        className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 border border-zinc-700"
                      >
                        <Mail size={13} /> Kombiniert 50 %
                      </button>
                    </div>
                    {invoicePanel2Open && ["AZ2","AZ3","AZ2_combined"].includes(invoicePanel2Kind) && (
                      <InvoicePanel2Inline
                        signedOffer={signedOffer}
                        project={project}
                        kind={invoicePanel2Kind}
                        percent={invoicePanel2Percent}
                        setPercent={setInvoicePanel2Percent}
                        email={invoicePanel2Email}
                        setEmail={setInvoicePanel2Email}
                        onSubmit={submitInvoicePanel2}
                        onCreateOnly={createInvoicePanel2Only}
                        onCancel={() => setInvoicePanel2Open(false)}
                        sending={invoiceSending}
                      />
                    )}
                  </div>
                )}
              </div>
            </li>
            </React.Fragment>
          );
        })}
      </ol>

      {publicUrl && (
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <div className="text-[12px] text-zinc-300 mb-1">Kunden-Tracking-Link:</div>
          <div className="text-[12px] font-mono text-zinc-300 bg-[#09090B] rounded-lg px-3 py-2 break-all" data-testid="public-tracking-url">
            {publicUrl}
          </div>
        </div>
      )}

      {/* Iter88zi (2026-02-19) — Manuelle Freigabe vor jedem Rechnungs-Versand. */}
      <EmailApprovalDialog
        projectId={project?.id}
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        onConfirm={(cc) => (
          approvalCtx?.isResend && approvalCtx?.invoiceId
            ? resendInvoiceEmail({
                invoiceId: approvalCtx.invoiceId,
                cc,
                recipientOverride: approvalCtx?.recipient,
              })
            : sendDownPaymentInvoice({
                sendEmail: true,
                kind: approvalCtx?.kind === "AZ1" ? undefined : approvalCtx?.kind,
                percent: approvalCtx?.percent,
                cc,
                recipientOverride: approvalCtx?.recipient,
              })
        )}
        recipient={approvalCtx?.recipient}
        subject={approvalCtx?.subject}
        attachments={approvalCtx?.attachments || []}
        bodyPreview={approvalCtx?.bodyPreview}
        defaultCc={user?.email ? [user.email] : []}
        testIdPrefix="invoice-approval"
        pdfFetcher={approvalCtx && signedOffer ? (
          approvalCtx.isResend && approvalCtx.invoiceId
            ? (async () => {
                // Resend: existierendes PDF aus Storage laden
                const r = await api.get(
                  `/offers/${signedOffer.id}/invoices/${approvalCtx.invoiceId}/pdf`,
                  { responseType: "blob" },
                );
                return r.data;
              })
            : (async () => {
                // Erst-Versand: Preview-Endpoint rendert PDF ohne Persistierung
                const body = { percent: approvalCtx.percent, send_email: false };
                if (approvalCtx.kind && approvalCtx.kind !== "AZ1") body.kind = approvalCtx.kind;
                const r = await api.post(
                  `/offers/${signedOffer.id}/down-payment-invoice/preview-pdf`,
                  body,
                  { responseType: "blob" },
                );
                return r.data;
              })
        ) : null}
      />

      {/* Iter88zl (2026-02-19) — Manuelle Freigabe vor Installateur-Übergabe. */}
      <EmailApprovalDialog
        open={handoverApprovalOpen}
        onClose={() => setHandoverApprovalOpen(false)}
        onConfirm={(cc) => sendHandover(cc)}
        recipient={handoverApprovalCtx?.recipient}
        subject={handoverApprovalCtx?.subject}
        attachments={handoverApprovalCtx?.attachments || []}
        bodyPreview={handoverApprovalCtx?.bodyPreview}
        defaultCc={user?.email ? [user.email] : []}
        testIdPrefix="handover-approval"
      />

      {/* Iter88zk (2026-02-19) — Reopen-Bestätigungs-Modal (Undo). */}
      {reopenStage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="stage-reopen-modal">
          <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw size={18} className="text-amber-400" />
              <h2 className="font-display font-bold text-lg text-zinc-100">Stufe wieder öffnen</h2>
            </div>
            <p className="text-[13px] text-zinc-300 mb-3">
              Du bist dabei die Stufe <b>„{reopenStage.label}"</b> wieder zu öffnen. Der Zeitstempel „abgeschlossen am …" wird entfernt und die Aktion in der Historie protokolliert.
            </p>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1">Grund (optional)</label>
            <textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="z. B. Rechnung war fehlerhaft, muss neu versendet werden"
              className="w-full h-20 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 text-[13px] resize-none focus:outline-none focus:border-amber-500/50"
              data-testid="stage-reopen-reason"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setReopenStage(null); setReopenReason(""); }} disabled={reopenSaving}
                      className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 rounded-lg text-[13px] font-bold disabled:opacity-40"
                      data-testid="stage-reopen-cancel">Abbrechen</button>
              <button onClick={doReopen} disabled={reopenSaving}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-lg text-[13px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40"
                      data-testid="stage-reopen-confirm">
                {reopenSaving ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                {reopenSaving ? "Öffne …" : "Wieder öffnen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
